/**
 * ============================================================================
 * SideButtons — View (UI only)
 * ============================================================================
 */
"use client";

import cn from "clsx";
import type { MouseEvent } from "react";
import updateImageDomain from "@/common/imageUrlReplace";
import UnivoImage from "@/components/common/UnivoImage";
import UnivoLink from "@/components/common/UnivoLink";
import styles from "../SideButtons.module.scss";
import {
  opensRfiForUrl,
  type SideButtonsViewModel,
} from "../model/sideButtons.types";

export default function SideButtonsView(vm: SideButtonsViewModel) {
  const {
    filterCallButton,
    isVisible,
    isMobileView,
    isMobileViewDisabled,
    fireGtEvents,
    handleClick,
    renderItemUrl,
  } = vm;

  return (
    <>
      <div
        className={styles.sideBtnRoot}
        data-sticky-sidetray
        style={{
          position: "fixed",
          right: 0,
          bottom: "20%",
          zIndex: 9997,
          boxSizing: "border-box",
          // Opacity (not visibility) so globals.scss can still hide the tray
          // while the programs menu is open.
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? "auto" : "none",
          transition: "opacity 0.2s ease",
        }}
        aria-hidden={!isVisible}
      >
        <div
          className={`sideButtons flex flex-col items-center ${styles.buttonPosition} ${styles.btnWrapper}`}
        >
          {filterCallButton?.map((item, i) => {
            const isCall = item.title === "call-button";
            const href = renderItemUrl(item?.url, item?.title)?.trim();
            // Blank or phone (tel:) url → open the RFI form instead of linking.
            // Desktop call button always opens it.
            const opensRfi = opensRfiForUrl(href) || (isCall && !isMobileView);
            return (
            <div className={styles.imgSize} key={i}>
              {opensRfi ? (
                <button
                  onClick={fireGtEvents}
                  className={styles.stickCTAbtn}
                  disabled={isMobileViewDisabled}
                  aria-label={isCall ? "Call us" : item?.alt || "Talk to us"}
                  id={isCall ? "call_us_sticky_cta" : `${item?.title}_sticky_cta`}
                >
                  <UnivoImage
                    id={isCall ? "call-icon" : `${item?.title}-icon`}
                    className={cn("aspect-square", styles.imgIconSize)}
                    src={updateImageDomain(item?.iconUrl)}
                    alt={item?.alt || (isCall ? "call us" : `${item?.title}-icon`)}
                    width={72}
                    height={72}
                    sizes="72px"
                    loading="lazy"
                    fetchPriority="low"
                  />
                </button>
              ) : (
                (() => {
                  const isExt =
                    /^https?:\/\//i.test(href) || href.startsWith("tel:");
                  const onLinkClick = (
                    e: MouseEvent<HTMLAnchorElement>,
                  ) => {
                    if (isMobileViewDisabled) {
                      e.preventDefault();
                    } else {
                      handleClick(item);
                    }
                  };
                  const icon = (
                    <UnivoImage
                      id={`${item?.title}-icon`}
                      className={cn("aspect-square", styles.imgIconSize)}
                      src={updateImageDomain(item?.iconUrl)}
                      alt={item?.alt || `${item?.title}-icon`}
                      width={72}
                      height={72}
                      sizes="72px"
                      loading="lazy"
                      fetchPriority="low"
                    />
                  );
                  if (isExt) {
                    return (
                      <UnivoLink
                        href={href}
                        external
                        openInNewTab={item?.openInNewTab}
                        onClick={onLinkClick}
                        id="whatts-app-journey"
                      >
                        {icon}
                      </UnivoLink>
                    );
                  }
                  return (
                    <UnivoLink
                      href={href}
                      onClick={onLinkClick}
                      id="whatts-app-journey"
                    >
                      {icon}
                    </UnivoLink>
                  );
                })()
              )}
            </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
